import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useGetFuckHamyan from './hooks/useGetFuckHamyan'
import useGatewayReturn from 'utilities/analytics/useGatewayReturn'

/**
 * The legacy landing page for a finished payment.
 *
 * It used to read the outcome from `GET /payment/transactions/<pk>/` — a v1
 * endpoint with no row for a v2 GatewayPayment — so it painted a blank screen
 * and `payment_succeeded` / `payment_failed` never fired. The verdict now
 * travels in the query string, and `useGatewayReturn` is what reads it.
 *
 * The v2 gateways send their payers to `/funds/` instead (a route that renders
 * on every bundle ever shipped, service worker or not), so what still arrives
 * here is the legacy v1 redirect — which carries no query string at all.
 *
 * A paramless visit therefore means "a payment finished and we do not know how
 * it went", which is NOT the same as "it failed". Claiming failure there would
 * tell someone who just paid successfully that their money did not go through
 * — so we send them to their funds instead, where the real balance is. The
 * failure card is reserved for a result we were actually told about.
 */
export default function Verify() {
    const navigate = useNavigate()
    const { mutate } = useGetFuckHamyan()

    const { result, isSuccess } = useGatewayReturn({
        onSuccess: ({ paymentCode }) => {
            const items = JSON.parse(
                localStorage.getItem('toBePaidTransactions') ?? 'null'
            )

            // Both spellings. The live gateway writers store the SINGULAR
            // `pay-for-other` and only the legacy wallet writer stores the
            // plural, so branching on the plural alone meant this never ran.
            const isPayForOther =
                items?.type === 'pay-for-other' ||
                items?.type === 'pay-for-others'
            const payload: IAddOnlinePayForOtherPayload | undefined =
                items?.data?.[0]

            // …but register the cashin only when the backend did NOT already
            // settle this payment. `GatewayPayment.make_successful()` pays
            // every PaymentItem itself, so posting the cashin again would
            // credit the fund twice for money paid once. A `code` in the query
            // string is exactly the marker of that v2 path; the legacy v1
            // redirect carries none and does leave the cashin to us.
            if (isPayForOther && payload && !paymentCode) {
                mutate(payload)
                return
            }

            // Same reasoning: /group-all-payment fires `group-all-payment/pay/`
            // on mount, which re-settles the whole basket out of the user's
            // WALLET. Harmless for the legacy flow that page was built for, a
            // double charge for a basket the gateway has just paid for.
            if (items?.type === 'group-all-payment' && !paymentCode) {
                navigate('/group-all-payment', {
                    state: { isWalletCharged: true },
                })
                return
            }

            // Nothing downstream of /funds reads it, and a record left behind
            // is a record the NEXT callback could act on.
            localStorage.removeItem('toBePaidTransactions')
            navigate('/funds')
        },
    })

    useEffect(() => {
        if (!result) {
            localStorage.removeItem('toBePaidTransactions')
            navigate('/funds', { replace: true })
        }
    }, [result, navigate])

    return (
        <div>
            {result && !isSuccess && (
                <div className=" h-screen flex flex-col justify-center">
                    <p className=" text-red-1 text-3xl text-center my-4">
                        پرداخت ناموفق
                    </p>
                    <button
                        className=" mx-2 border-1 border-red-1 bg-red-1 rounded-md py-3 text-white"
                        onClick={() => {
                            localStorage.removeItem('toBePaidTransactions')
                            navigate('/funds')
                        }}
                    >
                        بازگشت
                    </button>
                </div>
            )}
        </div>
    )
}
